/**
 * Citation-placement preprocessing for Bird's markdown, applied to PROSE only.
 *
 * Pure and dependency-free (no React, no Firebase-bearing import graph) so the
 * contract can be stated in strings and unit-tested without the component.
 */

/**
 * Spans the citation rewrite must never touch, matched in one pass so the
 * gaps between matches are exactly the prose:
 *
 *  - a fenced block: three or more backticks OR tildes at the start of a line
 *    (```, ~~~ and ```` are all CommonMark, and react-markdown renders each),
 *    info string and all. `[^\n]*` rather than `[\w-]*` so an R Markdown
 *    chunk header (```{r setup, include=FALSE}) is a fence too. The span
 *    starts at the line's leading indentation, not at the fence characters:
 *    a fence nested in a list item is indented, and that indentation is part
 *    of the fence, not prose to be rewritten. The closing fence is a run of
 *    the SAME character at least as long as the opener (so a ```` fence can
 *    hold a ``` line), on its own line, indented or not. An UNCLOSED fence
 *    runs to end of input: mid-stream the closing fence has not arrived yet,
 *    and rewriting the code until it does would show `m[1, 2]` as `m[1][2]`
 *    and then snap it back. `(?![\s\S])` is end of input; `$` under the `m`
 *    flag the line anchors need would be end of LINE.
 *  - an inline code span: a run of N backticks closed by exactly N, so
 *    ``a`b`` survives with its inner backtick. The body may contain a line
 *    ending but never a blank line (CommonMark ends a code span there):
 *    without that exclusion two stray backticks in different paragraphs
 *    protected every citation between them. `\n\n` only, not a
 *    whitespace-only line: a quantifier inside the starred group trips
 *    `detect-unsafe-regex`, and the cost of the narrower check is a missed
 *    citation fix in a shape that needs stray backticks AND an indented
 *    blank line. A fence still spans blank lines; at a fence the first
 *    alternative wins.
 *
 * Groups: 1 = the opening fence run, 2 = its character, 3 = the inline run.
 *
 * Exported for `mathDelimiters.ts`, which must skip the same code spans.
 * It is a global (`g`) regex: callers use `matchAll`, never `.test`/`.exec`,
 * so no `lastIndex` state leaks between calls.
 */
export const PROTECTED_SPAN_RE =
  /^[ \t]*(([`~])\2{2,})[^\n]*\n[\s\S]*?(?:^[ \t]*\1\2*[ \t]*$|(?![\s\S]))|(`+)(?!`)(?:[^`]|(?!\n\n)[^`](?:(?!\n\n)[\s\S])*?[^`])\3(?!`)/gm;

function fixCitationProse(prose: string): string {
  return (
    prose
      // Expand comma-separated citations like [3, 4] or [1,2,3] into [3][4] or [1][2][3]
      .replace(/\[([\d, ]+)\]/g, (_match, nums: string) => {
        const parts = nums
          .split(',')
          .map((n) => n.trim())
          .filter((n) => /^\d+$/.test(n));
        return parts.length >= 2 ? parts.map((n) => `[${n}]`).join('') : _match;
      })
      // Add space before citation if it directly follows a letter (but not after punctuation)
      .replace(/([a-zA-Z])(\[\d+\])/g, '$1 $2')
      // Add space after citation if it directly precedes a letter
      .replace(/(\[\d+\])([a-zA-Z])/g, '$1 $2')
  );
}

/**
 * Preprocess markdown content to fix citation placement issues.
 * The LLM sometimes places citations mid-word (e.g., "T[8][9]his" instead of "This[8][9]").
 * This function ensures proper spacing around citations.
 *
 * Code is left byte-for-byte: the same rewrites that repair a citation turn
 * `m[1, 2]` into `m[1][2]` and `x[1]` into `x [1]`, which is how "the R code
 * Bird wrote won't run" reached a customer. Fences and inline spans pass
 * through untouched; only the prose between them is rewritten.
 *
 * There is deliberately no whitespace clean-up on the prose either. The three
 * rewrites cannot leave a double space behind (each inserts exactly one space
 * between a letter and a bracket that were adjacent, and the expansion only
 * removes characters), so a trailing `.replace(/  +/g, ' ')` only ever reached
 * model output: it flattened a nested list's indentation into siblings and
 * deleted the two-space markdown hard break.
 */
export function preprocessCitationPlacement(content: string): string {
  let out = '';
  let last = 0;
  for (const m of content.matchAll(PROTECTED_SPAN_RE)) {
    out += fixCitationProse(content.slice(last, m.index)) + m[0];
    last = m.index + m[0].length;
  }
  return out + fixCitationProse(content.slice(last));
}
